import * as THREE from "three";

const EPSILON = 1e-12;

const normalizeMoment = (value, fallback = 1e-9) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
};

const fullInertiaToMatrix = (fullinertia) => {
  if (!Array.isArray(fullinertia) || fullinertia.length !== 6) return null;
  const values = fullinertia.map(Number);
  if (!values.every(Number.isFinite)) return null;
  const [ixx, iyy, izz, ixy, ixz, iyz] = values;
  return [
    [ixx, ixy, ixz],
    [ixy, iyy, iyz],
    [ixz, iyz, izz],
  ];
};

const matrixToFullInertia = (matrix) => [
  normalizeMoment(matrix[0][0]),
  normalizeMoment(matrix[1][1]),
  normalizeMoment(matrix[2][2]),
  Number(matrix[0][1]) || 0,
  Number(matrix[0][2]) || 0,
  Number(matrix[1][2]) || 0,
];

const identity3 = () => [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
];

const jacobiEigenSymmetric3 = (inputMatrix) => {
  const matrix = inputMatrix.map((row) => [...row]);
  const vectors = identity3();

  for (let iteration = 0; iteration < 32; iteration += 1) {
    let p = 0;
    let q = 1;
    let max = Math.abs(matrix[0][1]);

    const candidates = [
      [0, 2],
      [1, 2],
    ];
    candidates.forEach(([i, j]) => {
      const value = Math.abs(matrix[i][j]);
      if (value > max) {
        max = value;
        p = i;
        q = j;
      }
    });

    if (max < EPSILON) break;

    const app = matrix[p][p];
    const aqq = matrix[q][q];
    const apq = matrix[p][q];
    const tau = (aqq - app) / (2 * apq);
    const t = Math.sign(tau || 1) / (Math.abs(tau) + Math.sqrt(1 + tau * tau));
    const c = 1 / Math.sqrt(1 + t * t);
    const s = t * c;

    for (let k = 0; k < 3; k += 1) {
      if (k !== p && k !== q) {
        const mkp = matrix[k][p];
        const mkq = matrix[k][q];
        matrix[k][p] = c * mkp - s * mkq;
        matrix[p][k] = matrix[k][p];
        matrix[k][q] = s * mkp + c * mkq;
        matrix[q][k] = matrix[k][q];
      }
    }

    matrix[p][p] = c * c * app - 2 * s * c * apq + s * s * aqq;
    matrix[q][q] = s * s * app + 2 * s * c * apq + c * c * aqq;
    matrix[p][q] = 0;
    matrix[q][p] = 0;

    for (let k = 0; k < 3; k += 1) {
      const vkp = vectors[k][p];
      const vkq = vectors[k][q];
      vectors[k][p] = c * vkp - s * vkq;
      vectors[k][q] = s * vkp + c * vkq;
    }
  }

  const entries = [0, 1, 2]
    .map((index) => ({
      value: matrix[index][index],
      vector: [vectors[0][index], vectors[1][index], vectors[2][index]],
    }))
    .sort((a, b) => b.value - a.value);

  const xAxis = new THREE.Vector3(...entries[0].vector).normalize();
  const yAxis = new THREE.Vector3(...entries[1].vector).normalize();
  let zAxis = new THREE.Vector3(...entries[2].vector).normalize();
  const handedness = new THREE.Vector3().crossVectors(xAxis, yAxis).dot(zAxis);
  if (handedness < 0) zAxis = zAxis.multiplyScalar(-1);

  const matrix4 = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
  const orientation = new THREE.Quaternion().setFromRotationMatrix(matrix4).normalize();

  return {
    diagonal: entries.map((entry) => normalizeMoment(entry.value)),
    orientation,
  };
};

export const resolveInertialPrincipalAxes = (inertial) => {
  const diagonal = inertial?.diagonal || inertial?.diaginertia;
  if (Array.isArray(diagonal) && diagonal.length >= 3) {
    return {
      diagonal: [
        normalizeMoment(diagonal[0]),
        normalizeMoment(diagonal[1]),
        normalizeMoment(diagonal[2]),
      ],
      orientation: new THREE.Quaternion(),
      source: "diagonal",
    };
  }

  const matrix = fullInertiaToMatrix(inertial?.fullinertia);
  if (!matrix) return null;
  return {
    ...jacobiEigenSymmetric3(matrix),
    source: "full",
  };
};

export const diagonalInertiaToFullInertia = (diagonal, orientation) => {
  const moments = [
    normalizeMoment(diagonal?.[0]),
    normalizeMoment(diagonal?.[1]),
    normalizeMoment(diagonal?.[2]),
  ];
  const quat = Array.isArray(orientation)
    ? new THREE.Quaternion(
        orientation[1] || 0,
        orientation[2] || 0,
        orientation[3] || 0,
        orientation[0] || 1
      )
    : new THREE.Quaternion();
  quat.normalize();
  const rotation = new THREE.Matrix3().setFromMatrix4(
    new THREE.Matrix4().makeRotationFromQuaternion(quat)
  );
  const elements = rotation.elements;
  const r = [
    [elements[0], elements[3], elements[6]],
    [elements[1], elements[4], elements[7]],
    [elements[2], elements[5], elements[8]],
  ];
  const matrix = identity3().map((row, i) =>
    row.map(
      (_, j) =>
        r[i][0] * moments[0] * r[j][0] +
        r[i][1] * moments[1] * r[j][1] +
        r[i][2] * moments[2] * r[j][2]
    )
  );
  return matrixToFullInertia(matrix);
};

export const computeInertialVisualScale = (diagonal, mass, largestDim = 0) => {
  const [ixx, iyy, izz] = Array.isArray(diagonal) ? diagonal : [0.1, 0.1, 0.1];
  const safeMass = normalizeMoment(mass, 1.0);
  const baseX = Math.sqrt(Math.max(1e-9, (5 * normalizeMoment(ixx)) / safeMass));
  const baseY = Math.sqrt(Math.max(1e-9, (5 * normalizeMoment(iyy)) / safeMass));
  const baseZ = Math.sqrt(Math.max(1e-9, (5 * normalizeMoment(izz)) / safeMass));

  let factor = 2.0;
  if (largestDim > 1e-6) {
    const baseMax = Math.max(baseX, baseY, baseZ) || 1e-6;
    const targetFrac = 0.12;
    const minFrac = 0.06;
    const maxFrac = 0.2;
    const target = largestDim * targetFrac;
    const minTarget = largestDim * minFrac;
    const maxTarget = largestDim * maxFrac;
    factor = target / baseMax;
    factor = Math.max(minTarget / baseMax, Math.min(maxTarget / baseMax, factor));
  }

  return [baseX * factor, baseY * factor, baseZ * factor];
};
